import React, { useEffect, useState } from "react";
import { useAdminStore } from "../context/AdminContext";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";

const BASE_URL = process.env.REACT_APP_BASE_URL;

export default function AdminProfile() {
  const navigate = useNavigate();
  const { setSelectedAdmin, selectedAdmin } = useAdminStore();

  const [userDetails, setUserDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileImage, setProfileImage] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    fetchProfile();
    
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  /* FETCH PROFILE */
  const fetchProfile = async () => {
    try {
      const storedRole = localStorage.getItem("role");
      const adminToken =
        localStorage.getItem("adminToken") ||
        (storedRole && localStorage.getItem(`${storedRole}Token`)) ||
        localStorage.getItem("token");

      if (!adminToken) {
        setLoading(false);
        return;
      }

      let userName = localStorage.getItem("adminUsername");

      if (!userName) {
        try {
          const decoded = jwtDecode(adminToken);
          userName =
            decoded?.sub ||
            decoded?.username ||
            decoded?.userName ||
            decoded?.name ||
            null;
        } catch (err) {
          console.warn("JWT decode failed:", err);
        }
      }

      if (!userName) {
        setLoading(false);
        return;
      }

      const res = await axios.get(`${BASE_URL}/auth/profile`, {
        params: { userName },
        headers: { Authorization: `Bearer ${adminToken}` },
      });

      const profile = Array.isArray(res.data) ? res.data[0] : res.data;

      setUserDetails(profile);
      setSelectedAdmin(profile);
      localStorage.setItem("selectedAdmin", JSON.stringify(profile));

      if (profile?.image) {
        setProfileImage(
          profile.image.startsWith("http") ? profile.image : `${BASE_URL}/${profile.image}`
        );
      } else if (profile?.url) {
        setProfileImage(profile.url);
      }
    } catch (err) {
      console.log("Profile Fetch Error:", err);
    } finally {
      setLoading(false);
    }
  };

  /* IMAGE UPLOAD */
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !selectedAdmin?.id) return;

    const token =
      localStorage.getItem("adminToken") ||
      (localStorage.getItem("role") &&
        localStorage.getItem(`${localStorage.getItem("role")}Token`)) ||
      localStorage.getItem("token");

    const formData = new FormData();
    formData.append("imaged", file);

    try {
      const res = await axios.post(
        `${BASE_URL}/api/profile/upload/${selectedAdmin.id}/admin`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      const imageUrl = res.data;

      if (imageUrl) {
        const normalizedUrl = imageUrl.startsWith("http")
          ? imageUrl
          : `${BASE_URL}/${imageUrl}`;

        setProfileImage(normalizedUrl);

        const updatedAdmin = {
          ...selectedAdmin,
          url: normalizedUrl,
        };

        setSelectedAdmin(updatedAdmin);
        localStorage.setItem("selectedAdmin", JSON.stringify(updatedAdmin));
      }
    } catch (err) {
      console.log("Image Upload Error:", err.response?.data || err.message);
    }
  };

  const openImage = () => {
    if (profileImage) {
      window.open(profileImage, "_blank");
    }
  };

  if (loading) {
    return (
      <div className="responsive-loading">
        <div className="responsive-spinner"></div>
        <p>Loading profile...</p>
      </div>
    );
  }

  if (!userDetails) {
    return (
      <div className="responsive-error">
        <i className="fas fa-exclamation-triangle"></i>
        <h3>Unable to load profile</h3>
        <p>Please try logging in again.</p>
        <button 
          className="responsive-error-btn"
          onClick={() => navigate("/login")}
        >
          Go to Login
        </button>
      </div>
    );
  }

  const detailItems = [
    { label: "Admin ID", value: userDetails.id },
    { 
      label: "Username", 
      value: userDetails.adminUsername || userDetails.username || localStorage.getItem("adminUsername") 
    },
    { label: "Email", value: userDetails.adminEmail },
    { label: "Contact No", value: userDetails.contactNo },
    { 
      label: "Role", 
      value: (
        <span className="responsive-role-badge">
          {(userDetails.role || localStorage.getItem("role") || "").toUpperCase()}
        </span>
      )
    },
  ];

  return (
    <div className="responsive-container">
      {/* Header */}
      <header className="responsive-header">
        <div className="responsive-header-content">
          <h1 className="responsive-title">
            {!isMobile && <i className="fas fa-user-shield"></i>}
            <span>Admin Profile</span>
          </h1>
          <p className="responsive-subtitle">
            Your professional identity and account information
          </p>
        </div>
      </header>

      {/* Profile Card */}
      <section className="responsive-card">
        <div className="responsive-layout">
          {/* Avatar Section */}
          <div className="responsive-avatar-section">
            <div className="responsive-avatar-wrapper">
              <img
                src={
                  profileImage ||
                  "https://cdn-icons-png.flaticon.com/512/149/149071.png"
                }
                alt="Admin Profile"
                className="responsive-avatar-image"
                onClick={openImage}
              />
              <label 
                className="responsive-upload-overlay" 
                htmlFor="responsiveImageInput"
                title="Upload new photo"
              >
                <i className="fas fa-camera"></i>
              </label>
            </div>
            <input
              id="responsiveImageInput"
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              style={{ display: "none" }}
            />
            <h2 className="responsive-name">
              {userDetails.adminName || "Administrator"}
            </h2>
            <p className="responsive-role">System Administrator</p>
          </div>

          {/* Details Section */}
          <div className="responsive-details-section">
            <div className="responsive-details-grid">
              {detailItems.map((item, index) => (
                <div className="responsive-detail-item" key={index}>
                  <span className="responsive-detail-label">{item.label}</span>
                  <span className="responsive-detail-value">{item.value}</span>
                </div>
              ))}
            </div>

            <div className="responsive-action-section">
              <button
                className="responsive-edit-button"
                onClick={() => {
                  setSelectedAdmin(userDetails);
                  navigate("/adminedit");
                }}
              >
                <i className="fas fa-edit"></i> 
                <span>Edit Profile</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      <style jsx>{`
        /* Base Styles (Mobile First) */
        .responsive-container {
          min-height: 100vh;
          background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
          color: #e2e8f0;
          padding: 16px;
        }

        .responsive-header {
          text-align: center;
          margin-bottom: 32px;
          padding: 0 8px;
        }

        .responsive-header-content {
          max-width: 1200px;
          margin: 0 auto;
        }

        .responsive-title {
          font-size: 1.75rem;
          font-weight: 800;
          color: #60a5fa;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          margin-bottom: 8px;
          text-shadow: 0 2px 8px rgba(96, 165, 250, 0.4);
          flex-wrap: wrap;
        }

        .responsive-title i {
          font-size: 1.5rem;
        }

        .responsive-subtitle {
          font-size: 0.95rem;
          opacity: 0.9;
          color: #cbd5e1;
          line-height: 1.4;
        }

        .responsive-card {
          background: rgba(30, 41, 59, 0.95);
          backdrop-filter: blur(16px);
          border-radius: 20px;
          padding: 24px 20px;
          max-width: 1200px;
          margin: 0 auto;
          border: 1px solid rgba(59, 130, 246, 0.4);
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
        }

        .responsive-layout {
          display: flex;
          flex-direction: column;
          gap: 32px;
          align-items: center;
        }

        .responsive-avatar-section {
          text-align: center;
          width: 100%;
        }

        .responsive-avatar-wrapper {
          position: relative;
          width: 160px;
          height: 160px;
          margin: 0 auto 24px;
        }

        .responsive-avatar-image {
          width: 100%;
          height: 100%;
          border-radius: 50%;
          object-fit: cover;
          border: 4px solid #3b82f6;
          box-shadow: 0 8px 25px rgba(59, 130, 246, 0.4);
          transition: all 0.3s ease;
          cursor: pointer;
        }

        .responsive-avatar-image:hover {
          transform: scale(1.05);
          box-shadow: 0 12px 30px rgba(59, 130, 246, 0.6);
        }

        .responsive-upload-overlay {
          position: absolute;
          bottom: 8px;
          right: 8px;
          background: linear-gradient(135deg, #3b82f6, #2563eb);
          color: white;
          width: 44px;
          height: 44px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          box-shadow: 0 4px 15px rgba(59, 130, 246, 0.5);
          transition: all 0.3s ease;
          font-size: 1.1rem;
          z-index: 2;
        }

        .responsive-upload-overlay:hover {
          transform: translateY(-2px) scale(1.05);
          box-shadow: 0 6px 20px rgba(59, 130, 246, 0.7);
        }

        .responsive-name {
          font-size: 1.5rem;
          font-weight: 800;
          color: white;
          margin-bottom: 6px;
          line-height: 1.3;
        }

        .responsive-role {
          font-size: 1rem;
          color: #94a3b8;
          font-weight: 500;
        }

        .responsive-details-section {
          width: 100%;
        }

        .responsive-details-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 16px;
          margin-bottom: 32px;
        }

        .responsive-detail-item {
          background: rgba(15, 23, 42, 0.6);
          padding: 16px;
          border-radius: 12px;
          border: 1px solid rgba(59, 130, 246, 0.3);
          transition: all 0.3s ease;
          display: flex;
          flex-direction: column;
        }

        .responsive-detail-item:hover {
          background: rgba(15, 23, 42, 0.8);
          transform: translateY(-2px);
          box-shadow: 0 6px 15px rgba(59, 130, 246, 0.15);
        }

        .responsive-detail-label {
          font-size: 0.85rem;
          color: #94a3b8;
          margin-bottom: 6px;
          font-weight: 600;
          letter-spacing: 0.5px;
        }

        .responsive-detail-value {
          font-size: 1rem;
          font-weight: 700;
          color: white;
          line-height: 1.4;
          word-break: break-word;
        }

        .responsive-role-badge {
          display: inline-block;
          background: linear-gradient(135deg, #10b981, #059669);
          color: white;
          padding: 8px 16px;
          border-radius: 24px;
          font-weight: 700;
          font-size: 0.875rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
        }

        .responsive-action-section {
          text-align: center;
          width: 100%;
        }

        .responsive-edit-button {
          background: linear-gradient(135deg, #3b82f6, #2563eb);
          color: white;
          padding: 16px 28px;
          border: none;
          border-radius: 50px;
          font-size: 1rem;
          font-weight: 700;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          width: 100%;
          max-width: 280px;
          box-shadow: 0 8px 20px rgba(59, 130, 246, 0.4);
          transition: all 0.3s ease;
        }

        .responsive-edit-button:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 25px rgba(59, 130, 246, 0.6);
        }

        .responsive-edit-button i {
          font-size: 1rem;
        }

        /* Loading & Error States */
        .responsive-loading,
        .responsive-error {
          min-height: 100vh;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          color: #94a3b8;
          text-align: center;
          padding: 20px;
          background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
        }

        .responsive-spinner {
          width: 60px;
          height: 60px;
          border: 5px solid #1e293b;
          border-top: 5px solid #60a5fa;
          border-radius: 50%;
          animation: responsive-spin 1s linear infinite;
          margin-bottom: 20px;
        }

        @keyframes responsive-spin {
          to { transform: rotate(360deg); }
        }

        .responsive-error i {
          font-size: 3.5rem;
          color: #ef4444;
          margin-bottom: 20px;
          opacity: 0.8;
        }

        .responsive-error h3 {
          font-size: 1.5rem;
          color: white;
          margin-bottom: 12px;
        }

        .responsive-error p {
          font-size: 1rem;
          margin-bottom: 24px;
          opacity: 0.8;
        }

        .responsive-error-btn {
          background: linear-gradient(135deg, #3b82f6, #2563eb);
          color: white;
          padding: 12px 24px;
          border: none;
          border-radius: 8px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .responsive-error-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 15px rgba(59, 130, 246, 0.4);
        }

        /* Tablet Styles (min-width: 768px) */
        @media (min-width: 768px) {
          .responsive-container {
            padding: 24px;
          }

          .responsive-header {
            margin-bottom: 40px;
          }

          .responsive-title {
            font-size: 2.25rem;
          }

          .responsive-title i {
            font-size: 2rem;
          }

          .responsive-subtitle {
            font-size: 1.1rem;
          }

          .responsive-card {
            padding: 32px;
            border-radius: 24px;
          }

          .responsive-layout {
            flex-direction: row;
            align-items: flex-start;
            gap: 48px;
          }

          .responsive-avatar-section {
            flex: 1;
            min-width: 200px;
          }

          .responsive-avatar-wrapper {
            width: 180px;
            height: 180px;
          }

          .responsive-upload-overlay {
            width: 48px;
            height: 48px;
            font-size: 1.2rem;
          }

          .responsive-name {
            font-size: 1.75rem;
          }

          .responsive-role {
            font-size: 1.1rem;
          }

          .responsive-details-section {
            flex: 2;
          }

          .responsive-details-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 20px;
          }

          .responsive-detail-label {
            font-size: 0.9rem;
          }

          .responsive-detail-value {
            font-size: 1.1rem;
          }

          .responsive-role-badge {
            padding: 10px 20px;
            font-size: 0.95rem;
          }

          .responsive-action-section {
            text-align: right;
          }

          .responsive-edit-button {
            width: auto;
            padding: 16px 32px;
          }
        }

        /* Desktop Styles (min-width: 1024px) */
        @media (min-width: 1024px) {
          .responsive-container {
            padding: 30px;
          }

          .responsive-title {
            font-size: 2.6rem;
          }

          .responsive-card {
            padding: 40px;
            border-radius: 28px;
          }

          .responsive-layout {
            gap: 60px;
          }

          .responsive-avatar-wrapper {
            width: 220px;
            height: 220px;
          }

          .responsive-upload-overlay {
            width: 56px;
            height: 56px;
            font-size: 1.4rem;
          }

          .responsive-name {
            font-size: 2rem;
          }

          .responsive-details-grid {
            gap: 24px;
          }

          .responsive-detail-label {
            font-size: 0.95rem;
          }

          .responsive-detail-value {
            font-size: 1.2rem;
          }
        }

        /* Large Desktop Styles (min-width: 1440px) */
        @media (min-width: 1440px) {
          .responsive-container {
            padding: 40px;
          }

          .responsive-avatar-wrapper {
            width: 240px;
            height: 240px;
          }

          .responsive-details-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 28px;
          }

          .responsive-detail-item {
            padding: 24px;
          }
        }

        /* Small Mobile Optimization (max-width: 360px) */
        @media (max-width: 360px) {
          .responsive-container {
            padding: 12px;
          }

          .responsive-header {
            padding: 0 4px;
          }

          .responsive-title {
            font-size: 1.5rem;
            flex-direction: column;
            gap: 8px;
          }

          .responsive-card {
            padding: 20px 16px;
            border-radius: 16px;
          }

          .responsive-avatar-wrapper {
            width: 140px;
            height: 140px;
          }

          .responsive-upload-overlay {
            width: 40px;
            height: 40px;
            font-size: 1rem;
          }

          .responsive-name {
            font-size: 1.3rem;
          }

          .responsive-detail-item {
            padding: 14px;
          }

          .responsive-edit-button {
            padding: 14px 24px;
            font-size: 0.95rem;
          }
        }

        /* Landscape Mode Optimization */
        @media (max-height: 600px) and (orientation: landscape) {
          .responsive-container {
            min-height: auto;
            padding: 20px;
          }

          .responsive-layout {
            flex-direction: row;
            align-items: center;
          }

          .responsive-avatar-wrapper {
            width: 120px;
            height: 120px;
          }
        }

        /* Print Styles */
        @media print {
          .responsive-container {
            background: white;
            color: black;
          }

          .responsive-card {
            background: white;
            border: 1px solid #ddd;
            box-shadow: none;
          }

          .responsive-avatar-image {
            border-color: #333;
          }

          .responsive-upload-overlay {
            display: none;
          }

          .responsive-edit-button {
            display: none;
          }
        }
      `}</style>
    </div>
  );
}